import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Typography, Tag, Modal, Form, Input, InputNumber, Select, Popconfirm, Checkbox } from 'antd';
import { PlusCircleOutlined, EditOutlined } from '@ant-design/icons';
import { usePromotionService } from '../../services/pointofsale/PromotionServiceProvider';
import { usePointOfSaleService } from '../../services/pointofsale/PointOfSaleServiceProvider';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import { useCurrencySymbol } from '../../hooks/useFormatters';
import { PointOfSaleItem } from '../../model/pointofsale/PointOfSaleItem';
import { MembershipCategoryRow } from '../../model/members/MembershipCategoryRow';
import { MembershipCategory } from '../../model/members/MembershipCategory';
import {
    Promotion,
    PROMOTION_TYPE_MEMBERSHIP,
    asMembershipConfig,
} from '../../model/pointofsale/Promotion';
import MembershipCategorySelector from '../membership/MembershipCategorySelector';
import PosItemFormModal from './PosItemFormModal';

const { Title } = Typography;

// Sentinel Select value that opens the inline "create POS item" modal instead of selecting.
const CREATE_POS_ITEM = '__create_pos_item__';

// Small custom form control: MembershipCategorySelector wired to a form field that holds the
// category NAME (the selector's value), translating its object onChange back to the name.
const CategorySelect: React.FC<{
    value?: string;
    onChange?: (value: string) => void;
    categories: MembershipCategory[];
    id?: string;
}> = ({ value, onChange, categories, id }) => (
    <MembershipCategorySelector
        id={id}
        membershipCategories={categories}
        value={value}
        onChange={(category) => onChange?.(category?.name ?? '')}
    />
);

// Small custom form control: a POS-item dropdown plus a "+ Create new POS item" option. Picking the
// create option calls onCreateNew rather than selecting it.
const PosItemSelect: React.FC<{
    value?: string;
    onChange?: (value: string) => void;
    posItems: PointOfSaleItem[];
    onCreateNew: () => void;
    id?: string;
}> = ({ value, onChange, posItems, onCreateNew, id }) => {
    const [open, setOpen] = useState(false);
    return (
        <Select
            id={id}
            open={open}
            onDropdownVisibleChange={setOpen}
            placeholder="Select a POS item"
            value={value || undefined}
            onChange={(next) => {
                if (next === CREATE_POS_ITEM) {
                    // Close this dropdown before opening the create modal so it doesn't linger behind it.
                    setOpen(false);
                    onCreateNew();
                    return;
                }
                onChange?.(next);
            }}
            options={[
                ...posItems.map((item) => ({ label: item.name, value: item.name })),
                { label: '+ Create new POS item', value: CREATE_POS_ITEM },
            ]}
        />
    );
};

// Context passed to a promotion type's config-field renderer.
interface ConfigFieldsContext {
    categories: MembershipCategory[];
    posItems: PointOfSaleItem[];
    currencySymbol: string;
    onCreatePosItem: () => void;
}

// A promotion type's form behaviour. Adding a future type (e.g. voucher) means adding one entry to
// PROMOTION_TYPE_SPECS — the screen renders its fields and (de)serialises its config generically.
interface PromotionTypeSpec {
    type: string;
    label: string;
    renderConfigFields: (context: ConfigFieldsContext) => React.ReactNode;
    // Form values -> the config object stored on the promotion. Returns an error message string when
    // a value can't be resolved (e.g. an unknown category).
    buildConfig: (
        values: Record<string, unknown>,
        categories: MembershipCategoryRow[],
    ) => { config: Record<string, unknown> } | { error: string };
    // Promotion -> the form values that populate the config sub-form when editing.
    toFormValues: (promotion: Promotion, categories: MembershipCategoryRow[]) => Record<string, unknown>;
}

const membershipSpec: PromotionTypeSpec = {
    type: PROMOTION_TYPE_MEMBERSHIP,
    label: 'Membership',
    renderConfigFields: ({ categories, posItems, currencySymbol, onCreatePosItem }) => (
        <>
            <Form.Item
                name={['config', 'memberCategoryName']}
                label="Membership category"
                rules={[{ required: true, message: 'Please select a membership category' }]}
            >
                <CategorySelect categories={categories} />
            </Form.Item>
            <Form.Item
                name={['config', 'fixedFee']}
                label={`Fixed fee (${currencySymbol})`}
                rules={[
                    { required: true, message: 'Please enter the fee' },
                    { type: 'number', min: 0, message: 'Fee must be 0 or greater' },
                ]}
            >
                <InputNumber
                    inputMode="decimal"
                    min={0}
                    step={0.01}
                    precision={2}
                    style={{ width: '100%' }}
                    addonBefore={currencySymbol}
                />
            </Form.Item>
            <Form.Item
                name={['config', 'durationMonths']}
                label="Duration (months)"
                rules={[
                    { required: true, message: 'Please enter the duration' },
                    { type: 'number', min: 1, message: 'Duration must be at least 1 month' },
                ]}
            >
                <InputNumber inputMode="numeric" min={1} step={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
                name={['config', 'posItemName']}
                label="POS item (payment target)"
                rules={[{ required: true, message: 'Please select a POS item' }]}
            >
                <PosItemSelect posItems={posItems} onCreateNew={onCreatePosItem} />
            </Form.Item>
            <Form.Item
                name={['config', 'snapToBillingCycle']}
                valuePropName="checked"
                extra="On: valid-to snaps forward to the next billing date. Off: ends exactly after the duration."
            >
                <Checkbox>Snap valid-to to next billing cycle</Checkbox>
            </Form.Item>
            {/* Pro-rata only applies while snapping; disable (and clear on save) it when snap is off. */}
            <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                    prev.config?.snapToBillingCycle !== cur.config?.snapToBillingCycle}
            >
                {({ getFieldValue }) => {
                    const snap = Boolean(getFieldValue(['config', 'snapToBillingCycle']));
                    return (
                        <Form.Item
                            name={['config', 'proRata']}
                            valuePropName="checked"
                            extra="Charges the snapped extra period at the category's normal price, on top of the fixed fee."
                        >
                            <Checkbox disabled={!snap}>Calculate pro-rata for the snapped period</Checkbox>
                        </Form.Item>
                    );
                }}
            </Form.Item>
        </>
    ),
    buildConfig: (values, categories) => {
        const config = (values.config ?? {}) as Record<string, unknown>;
        const categoryName = String(config.memberCategoryName ?? '');
        const category = categories.find((c) => c.name === categoryName);
        if (!category) {
            return { error: 'Please select a valid membership category.' };
        }
        const snapToBillingCycle = Boolean(config.snapToBillingCycle);
        return {
            config: {
                memberCategoryId: category.id,
                fixedFee: Number(config.fixedFee ?? 0),
                durationMonths: Number(config.durationMonths ?? 0),
                posItemName: String(config.posItemName ?? ''),
                snapToBillingCycle,
                // Pro-rata is meaningless without snapping — don't persist a stale true.
                proRata: snapToBillingCycle && Boolean(config.proRata),
            },
        };
    },
    toFormValues: (promotion, categories) => {
        const membership = asMembershipConfig(promotion);
        const category = categories.find((c) => c.id === membership?.memberCategoryId);
        return {
            memberCategoryName: category?.name ?? '',
            fixedFee: membership?.fixedFee ?? 0,
            durationMonths: membership?.durationMonths ?? 3,
            posItemName: membership?.posItemName ?? '',
            snapToBillingCycle: membership?.snapToBillingCycle ?? true,
            proRata: membership?.proRata ?? false,
        };
    },
};

const PROMOTION_TYPE_SPECS: Record<string, PromotionTypeSpec> = {
    [membershipSpec.type]: membershipSpec,
};

const DEFAULT_MEMBERSHIP_FORM = {
    fixedFee: 0,
    durationMonths: 3,
    memberCategoryName: '',
    posItemName: '',
    snapToBillingCycle: true,
    proRata: false,
};

const PromotionsManagement: React.FC = () => {
    const promotionService = usePromotionService();
    const posService = usePointOfSaleService();
    const memberService = useMemberService();
    const currencySymbol = useCurrencySymbol();

    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [posItems, setPosItems] = useState<PointOfSaleItem[]>([]);
    const [categories, setCategories] = useState<MembershipCategoryRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
    const [selectedType, setSelectedType] = useState<string>(PROMOTION_TYPE_MEMBERSHIP);
    const [isPosItemModalOpen, setIsPosItemModalOpen] = useState<boolean>(false);
    // Categories + POS items must be loaded before the modal is usable (the config form needs them
    // to resolve memberCategoryId and populate the edit form). Gates the Add/Edit actions.
    const [referenceLoaded, setReferenceLoaded] = useState<boolean>(false);
    const [form] = Form.useForm();

    const categoryOptions = useMemo<MembershipCategory[]>(
        () => categories.map((c) => ({
            name: c.name,
            amount: c.price,
            paymentFrequency: c.paymentFrequency,
            isJointMembership: c.isJointMembership,
            isVisible: c.isVisible,
        })),
        [categories],
    );

    const loadPromotions = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await promotionService.getPromotions();
            setPromotions(rows);
        } catch {
            // The API client raises the error notification; leave the list as it was rather than
            // blanking the table on a transient failure.
        } finally {
            setLoading(false);
        }
    }, [promotionService]);

    useEffect(() => {
        loadPromotions();
    }, [loadPromotions]);

    useEffect(() => {
        let cancelled = false;
        const loadReferenceData = async () => {
            try {
                const [items, cats] = await Promise.all([
                    posService.getPosItems(),
                    memberService.getMembershipCategoriesDetailed(),
                ]);
                if (cancelled) return;
                setPosItems(items);
                setCategories(cats);
                setReferenceLoaded(true);
            } catch {
                // The API client surfaces an error notification; leave referenceLoaded false so the
                // Add/Edit actions stay disabled rather than opening a modal that can't resolve data.
            }
        };
        loadReferenceData();
        return () => { cancelled = true; };
    }, [posService, memberService]);

    const openCreateModal = () => {
        setEditingPromotion(null);
        setSelectedType(PROMOTION_TYPE_MEMBERSHIP);
        form.resetFields();
        form.setFieldsValue({
            name: '',
            promotionType: PROMOTION_TYPE_MEMBERSHIP,
            config: { ...DEFAULT_MEMBERSHIP_FORM },
        });
        setIsModalOpen(true);
    };

    const openEditModal = (promotion: Promotion) => {
        setEditingPromotion(promotion);
        setSelectedType(promotion.promotionType);
        const spec = PROMOTION_TYPE_SPECS[promotion.promotionType];
        form.resetFields();
        form.setFieldsValue({
            name: promotion.name,
            promotionType: promotion.promotionType,
            config: spec ? spec.toFormValues(promotion, categories) : {},
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingPromotion(null);
    };

    const handleSubmit = async () => {
        let values: Record<string, unknown>;
        try {
            values = await form.validateFields();
        } catch {
            return; // validation errors surfaced on the form
        }

        const promotionType = String(values.promotionType ?? '');
        const spec = PROMOTION_TYPE_SPECS[promotionType];
        if (!spec) return;

        const built = spec.buildConfig(values, categories);
        if ('error' in built) {
            form.setFields([{ name: ['config', 'memberCategoryName'], errors: [built.error] }]);
            return;
        }

        const payload = {
            name: String(values.name ?? '').trim(),
            promotionType,
            config: built.config,
        };

        if (editingPromotion) {
            await promotionService.updatePromotion(editingPromotion.id, payload);
        } else {
            await promotionService.createPromotion(payload);
        }
        closeModal();
        await loadPromotions();
    };

    const handleDeactivate = async (promotion: Promotion) => {
        await promotionService.deletePromotion(promotion.id);
        await loadPromotions();
    };

    // A POS item created inline is added to the dropdown and selected into the promotion.
    const handlePosItemSaved = (item: PointOfSaleItem) => {
        setPosItems((prev) => {
            const idx = prev.findIndex((i) => i.id === item.id);
            if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = item;
                return updated;
            }
            return [...prev, item];
        });
        form.setFieldValue(['config', 'posItemName'], item.name);
        setIsPosItemModalOpen(false);
    };

    const activeSpec = PROMOTION_TYPE_SPECS[selectedType];

    return (
        <Space direction="vertical" style={{ width: '100%', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Promotions</Title>
                <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={openCreateModal}
                    disabled={!referenceLoaded}
                    data-testid="promotion-add-btn"
                >
                    Add Promotion
                </Button>
            </div>

            <Table
                dataSource={promotions}
                loading={loading}
                rowKey="id"
                data-testid="promotions-table"
                columns={[
                    { title: 'Name', dataIndex: 'name', key: 'name' },
                    { title: 'Type', dataIndex: 'promotionType', key: 'promotionType' },
                    {
                        title: 'Status',
                        dataIndex: 'isActive',
                        key: 'isActive',
                        render: (isActive: boolean) => (
                            <Tag color={isActive ? 'green' : 'default'}>{isActive ? 'Active' : 'Inactive'}</Tag>
                        ),
                    },
                    {
                        title: 'Action',
                        key: 'action',
                        render: (_, record: Promotion) => (
                            <Space>
                                <Button
                                    icon={<EditOutlined />}
                                    disabled={!referenceLoaded}
                                    onClick={() => openEditModal(record)}
                                />
                                {record.isActive && (
                                    <Popconfirm
                                        title="Deactivate this promotion?"
                                        onConfirm={() => handleDeactivate(record)}
                                        okText="Deactivate"
                                        cancelText="Cancel"
                                    >
                                        <Button danger>Deactivate</Button>
                                    </Popconfirm>
                                )}
                            </Space>
                        ),
                    },
                ]}
            />

            <Modal
                title={editingPromotion ? 'Edit Promotion' : 'Add Promotion'}
                open={isModalOpen}
                onOk={handleSubmit}
                onCancel={closeModal}
                okText="Save"
                cancelText="Cancel"
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Promotion name"
                        rules={[{ required: true, message: 'Please enter a promotion name' }]}
                    >
                        <Input placeholder="Enter promotion name" />
                    </Form.Item>

                    <Form.Item
                        name="promotionType"
                        label="Type"
                        rules={[{ required: true, message: 'Please select a type' }]}
                    >
                        <Select
                            onChange={(value) => setSelectedType(value)}
                            options={Object.values(PROMOTION_TYPE_SPECS).map((spec) => ({
                                label: spec.label,
                                value: spec.type,
                            }))}
                        />
                    </Form.Item>

                    {activeSpec?.renderConfigFields({
                        categories: categoryOptions,
                        posItems,
                        currencySymbol,
                        onCreatePosItem: () => setIsPosItemModalOpen(true),
                    })}
                </Form>
            </Modal>

            <PosItemFormModal
                open={isPosItemModalOpen}
                editingItem={null}
                existingItems={posItems}
                onSaved={handlePosItemSaved}
                onCancel={() => setIsPosItemModalOpen(false)}
            />
        </Space>
    );
};

export default PromotionsManagement;
